import { useState } from "react";
import { ShieldAlert, Download, KeyRound, Loader2, Cpu, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api, { API, formatApiError } from "@/lib/api";

export default function ActivationScreen({ info }) {
  const [key, setKey] = useState("");
  const [code, setCode] = useState("");
  const [murl, setMurl] = useState(info?.master_url || "");
  const [codeBusy, setCodeBusy] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [busy, setBusy] = useState(false);
  const [renewBusy, setRenewBusy] = useState(false);
  const m = info?.machine || {};

  const activate = async () => {
    if (!key.trim()) { toast.error("Wklej klucz licencyjny"); return; }
    setBusy(true);
    try {
      await api.post("/activation/apply", { license_key: key.trim(), master_url: murl.trim() });
      toast.success("Licencja aktywowana");
      setTimeout(() => window.location.reload(), 1200);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Aktywacja nieudana");
    } finally { setBusy(false); }
  };

  const activateCode = async () => {
    if (!code.trim()) { toast.error("Wpisz kod aktywacyjny"); return; }
    setCodeBusy(true);
    try {
      const { data } = await api.post("/activation/code", { code: code.trim(), master_url: murl.trim() });
      toast.success(`Licencja aktywowana${data.expires ? " do " + data.expires : ""}`);
      setTimeout(() => window.location.reload(), 1200);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Aktywacja nieudana");
    } finally { setCodeBusy(false); }
  };

  const renewOnline = async () => {
    setRenewBusy(true);
    try {
      const { data } = await api.post("/activation/renew");
      if (data.applied) {
        toast.success("Licencja odnowiona do " + (data.expires || "bezterminowa"));
        setTimeout(() => window.location.reload(), 1400);
      } else {
        toast(data.message || "Wysłano prośbę o odnowienie do dostawcy.");
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Nie udało się wysłać prośby");
    } finally { setRenewBusy(false); }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4" data-testid="activation-screen">
      <div className="w-full max-w-lg bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-2xl">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-11 h-11 rounded-xl bg-amber-500/15 flex items-center justify-center">
            <ShieldAlert className="text-amber-400" size={22} />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-white">Aktywacja licencji</h1>
            <p className="text-xs text-slate-400">{info?.reason || "Ten serwer wymaga aktywacji licencji."}</p>
          </div>
        </div>

        <div className="mt-4 bg-slate-950 border border-slate-800 rounded-lg p-3" data-testid="activation-machine">
          <div className="flex items-center gap-2 text-xs font-semibold text-slate-300 mb-1.5"><Cpu size={13} className="text-cyan-400" /> Ten komputer</div>
          <div className="text-[11px] text-slate-400 space-y-0.5 font-mono break-all">
            <div>ID sprzętu: <span className="text-slate-200">{m.fingerprint}</span></div>
            <div>System: <span className="text-slate-200">{m.os} {m.os_version}</span></div>
            {m.hostname && <div>Host: <span className="text-slate-200">{m.hostname}</span></div>}
          </div>
        </div>

        {typeof info?.days_left === "number" && (
          <div data-testid="activation-expiry" className={`mt-3 text-xs rounded-lg px-3 py-2 border ${info.days_left < 0 ? "bg-red-950/40 border-red-800 text-red-300" : "bg-amber-950/30 border-amber-800 text-amber-300"}`}>
            {info.days_left < 0
              ? `Licencja wygasła ${Math.abs(info.days_left)} dni temu — wymagane odnowienie.`
              : `Licencja ważna jeszcze ${info.days_left} dni.`}
          </div>
        )}

        {!info?.master_url && (
          <div className="mt-4">
            <label className="text-xs font-semibold text-slate-300 mb-1 block">Adres serwera dostawcy</label>
            <input value={murl} onChange={(e) => setMurl(e.target.value)} placeholder="np. https://fiberpass.pl" data-testid="activation-master-url"
              className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-xs text-white outline-none focus:ring-1 focus:ring-cyan-500" />
          </div>
        )}

        <div className="mt-4 rounded-lg border border-cyan-800 bg-cyan-950/20 p-3" data-testid="activation-code-box">
          <div className="text-xs font-semibold text-cyan-200 mb-1">Aktywacja kodem (zalecane)</div>
          <p className="text-[11px] text-slate-400 mb-2">Wpisz kod otrzymany od dostawcy — licencja zostanie pobrana i przypisana do tego serwera automatycznie.</p>
          <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="FP-XXXX-XXXX" data-testid="activation-code-input"
            className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm font-mono tracking-wider text-white outline-none focus:ring-1 focus:ring-cyan-500" />
          <button type="button" onClick={activateCode} disabled={codeBusy} data-testid="activation-code-submit"
            className="mt-2 w-full inline-flex items-center justify-center gap-2 bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 text-white rounded-lg py-2.5 text-sm font-medium">
            {codeBusy ? <Loader2 size={15} className="animate-spin" /> : <KeyRound size={15} />} Aktywuj kodem
          </button>
        </div>

        <button type="button" onClick={() => setShowKey((s) => !s)} className="mt-3 text-[11px] text-slate-400 hover:text-cyan-300 underline" data-testid="activation-toggle-key">
          {showKey ? "Ukryj" : "Mam klucz licencyjny lub plik aktywacyjny"}
        </button>
        {showKey && (<>
        <ol className="mt-3 text-[12px] text-slate-300 space-y-2 list-decimal list-inside">
          <li>Pobierz plik aktywacyjny i wyślij go do dostawcy.</li>
          <li>Otrzymasz klucz licencyjny przypisany do tego sprzętu.</li>
          <li>Wklej klucz poniżej i kliknij „Aktywuj".</li>
        </ol>

        <a href={`${API}/activation/request`} data-testid="activation-download-req"
          className="mt-3 inline-flex items-center gap-2 bg-slate-800 hover:bg-slate-700 text-slate-100 rounded-lg px-3 py-2 text-sm">
          <Download size={15} /> Pobierz plik aktywacyjny (.fpreq)
        </a>

        <div className="mt-4">
          <label className="text-xs font-semibold text-slate-300 mb-1 block">Klucz licencyjny</label>
          <textarea value={key} onChange={(e) => setKey(e.target.value)} rows={3} data-testid="activation-key-input"
            placeholder="FPL1.xxxxxxxx.xxxxxxxx"
            className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-xs font-mono text-white outline-none focus:ring-1 focus:ring-cyan-500 resize-none" />
        </div>
        <button type="button" onClick={activate} disabled={busy} data-testid="activation-submit"
          className="mt-3 w-full inline-flex items-center justify-center gap-2 bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 text-white rounded-lg py-2.5 text-sm font-medium">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <KeyRound size={15} />} Aktywuj
        </button>
        </>)}

        <div className="mt-4 border-t border-slate-800 pt-4">
          <p className="text-[11px] text-slate-400 mb-2">Odnowienie jest płatne. Wyślij prośbę do dostawcy — po zaksięgowaniu opłaty zatwierdzi przedłużenie, a Ty pobierzesz nową licencję tym samym przyciskiem (bez plików).</p>
          <button type="button" onClick={renewOnline} disabled={renewBusy} data-testid="activation-renew-online"
            className="w-full inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white rounded-lg py-2.5 text-sm font-medium">
            {renewBusy ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />} Poproś / pobierz odnowienie
          </button>
        </div>
      </div>
    </div>
  );
}
